import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { leadService } from '../../services/api';

// Initial state
const initialState = {
  leads: [],
  lead: null,
  activities: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 10,
    totalCount: 0,
  },
};

// Async thunks
export const fetchLeads = createAsyncThunk(
  'leads/fetchLeads',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await leadService.getLeads(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchLead = createAsyncThunk(
  'leads/fetchLead',
  async (id, { rejectWithValue }) => {
    try {
      const response = await leadService.getLead(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createLead = createAsyncThunk(
  'leads/createLead',
  async (data, { rejectWithValue }) => {
    try {
      const response = await leadService.createLead(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateLead = createAsyncThunk(
  'leads/updateLead',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await leadService.updateLead(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteLead = createAsyncThunk(
  'leads/deleteLead',
  async (id, { rejectWithValue }) => {
    try {
      await leadService.deleteLead(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchLeadActivities = createAsyncThunk(
  'leads/fetchLeadActivities',
  async (id, { rejectWithValue }) => {
    try {
      const response = await leadService.getLeadActivities(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Lead slice
const leadSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    clearLeadError: (state) => {
      state.error = null;
    },
    clearLead: (state) => {
      state.lead = null;
      state.activities = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch leads
      .addCase(fetchLeads.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leads = action.payload.results || action.payload;
        // Handle pagination if API returns pagination data
        if (action.payload.count !== undefined) {
          state.pagination.totalCount = action.payload.count;
        }
        state.error = null;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch leads';
      })
      // Fetch single lead
      .addCase(fetchLead.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLead.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lead = action.payload;
        state.error = null;
      })
      .addCase(fetchLead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch lead';
      })
      // Create lead
      .addCase(createLead.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leads.unshift(action.payload);
        state.error = null;
      })
      .addCase(createLead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create lead';
      })
      // Update lead
      .addCase(updateLead.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the lead in the list if it exists
        state.leads = state.leads.map((lead) =>
          lead.id === action.payload.id ? action.payload : lead
        );
        // Update the current lead if it's the one being viewed
        if (state.lead && state.lead.id === action.payload.id) {
          state.lead = action.payload;
        }
        state.error = null;
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update lead';
      })
      // Delete lead
      .addCase(deleteLead.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leads = state.leads.filter((lead) => lead.id !== action.payload);
        // Clear the current lead if it's the one being deleted
        if (state.lead && state.lead.id === action.payload) {
          state.lead = null;
        }
        state.error = null;
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete lead';
      })
      // Fetch lead activities
      .addCase(fetchLeadActivities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeadActivities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activities = action.payload;
        state.error = null;
      })
      .addCase(fetchLeadActivities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch lead activities';
      });
  },
});

export const { clearLeadError, clearLead } = leadSlice.actions;
export default leadSlice.reducer;
